import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { useLanguage } from "@/contexts/LanguageContext";
import { Sparkles, RefreshCw, Copy, Check, Palette, Code, Box, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PrototypeStep = ({ project, setProject, saveProject, goToStep }) => {
  const { t, language } = useLanguage();
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [prototypeData, setPrototypeData] = useState(null);
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef(null);

  useEffect(() => {
    if (project?.prototype_prompt) {
      // Handle both old format (string) and new format (object)
      if (typeof project.prototype_prompt === "string") {
        setPrototypeData({ prompt: project.prototype_prompt });
      } else {
        setPrototypeData(project.prototype_prompt);
      }
      setHasGenerated(true);
    }
  }, []);

  const generatePrototype = async () => {
    if (!project?.concept) {
      toast.error(t('common_error'));
      return;
    }

    setIsGenerating(true);
    try {
      const response = await fetch(`${API}/generate/prototype-prompt`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          problem_statement: project.problem_statement,
          selected_idea: project.selected_idea,
          concept: project.concept,
          value_proposition: project.value_proposition,
          business_model: project.business_model,
          language,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setPrototypeData(data);
        setHasGenerated(true);
        
        await saveProject({ 
          prototype_prompt: data,
          current_step: 6
        }, false);
        
        setProject(prev => ({ ...prev, prototype_prompt: data }));
        toast.success(t('common_success'));
      } else {
        const error = await response.json();
        toast.error(error.detail || t('common_error'));
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error(t('common_error'));
    } finally {
      setIsGenerating(false);
    }
  };

  const copyPrompt = async () => {
    if (!prototypeData?.prompt) {
      toast.error(t('common_error'));
      return;
    }

    try {
      // Prefer copying from the actual textarea selection to align with some browser restrictions
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.select();

        const successful = document.execCommand("copy");
        if (!successful && navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(prototypeData.prompt);
        }
      } else if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(prototypeData.prompt);
      } else {
        const tempTextArea = document.createElement("textarea");
        tempTextArea.value = prototypeData.prompt;
        tempTextArea.style.position = "fixed";
        tempTextArea.style.opacity = "0";
        document.body.appendChild(tempTextArea);
        tempTextArea.focus();
        tempTextArea.select();
        document.execCommand("copy");
        document.body.removeChild(tempTextArea);
      }

      setCopied(true);
      toast.success(t('prototype_copied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Copy error:", error);
      toast.error(t('common_error'));
    }
  };

  const handleContinue = async () => {
    await saveProject({ prototype_prompt: prototypeData, current_step: 6 });
    goToStep(7);
  };

  const getTypeIcon = (type) => {
    const icons = {
      product_visual: Box,
      app_interface: Code,
      web_interface: Code,
      service_touchpoint: Palette,
    };
    return icons[type] || Palette;
  };

  return (
    <div className="h-full flex flex-col overflow-auto">
      {/* Header */}
      <div className="bg-white border-b border-stone-200 px-4 sm:px-8 py-4 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h2 className="font-heading text-2xl font-semibold text-stone-900">
              {t('prototype_title')}
            </h2>
            <p className="text-stone-600">
              {t('prototype_description')}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={generatePrototype}
              disabled={isGenerating}
              variant={hasGenerated ? "outline" : "default"}
              className={hasGenerated ? "btn-secondary" : "btn-primary"}
              data-testid="generate-prototype-btn"
            >
              {isGenerating ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  {t('common_generating')}
                </>
              ) : hasGenerated ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {t('prototype_regenerate')}
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  {t('prototype_generate')}
                </>
              )}
            </Button>
            {hasGenerated && (
              <Button
                onClick={handleContinue}
                className="btn-primary"
                data-testid="continue-to-presentation-btn"
              >
                {t('prototype_continue')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 p-4 sm:p-8">
        <div className="max-w-4xl mx-auto">
          {!hasGenerated ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 bg-accent-purple rounded-full flex items-center justify-center mx-auto mb-6">
                <Palette className="w-10 h-10 text-purple-600" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-stone-900 mb-2">
                {t('prototype_generate')}
              </h3>
              <p className="text-stone-600 mb-6 max-w-md mx-auto">
                {t('prototype_description')}
              </p>
              <Button
                onClick={generatePrototype}
                disabled={isGenerating}
                className="btn-primary"
                data-testid="generate-prototype-empty-btn"
              >
                {isGenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    {t('common_generating')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    {t('prototype_generate')}
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Prompt Card */}
              <Card className="p-6 border-stone-200" data-testid="prototype-prompt-card">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    {(() => {
                      const TypeIcon = getTypeIcon(prototypeData?.prompt_type);
                      return (
                        <div className="w-10 h-10 bg-accent-purple rounded-lg flex items-center justify-center">
                          <TypeIcon className="w-5 h-5 text-purple-600" />
                        </div>
                      );
                    })()}
                    <div>
                      <h3 className="font-heading font-semibold text-stone-900">
                        {t('prototype_title')}
                      </h3>
                      <span className="text-xs text-stone-500 uppercase">
                        {prototypeData?.prompt_type?.replace("_", " ")} • {prototypeData?.fidelity}
                      </span>
                    </div>
                  </div>
                  <Button
                    onClick={copyPrompt}
                    variant="outline"
                    size="sm"
                    className="btn-secondary"
                    data-testid="copy-prompt-btn"
                  >
                    {copied ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        {t('prototype_copied')}
                      </>
                    ) : (
                      <>
                        <Copy className="w-4 h-4 mr-2" />
                        {t('prototype_copy')}
                      </>
                    )}
                  </Button>
                </div>
                <Textarea
                  ref={textareaRef}
                  value={prototypeData?.prompt || ""}
                  readOnly
                  className="min-h-[200px] bg-stone-50 text-stone-700 font-mono text-sm"
                />
              </Card>

              {/* Details Grid */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Context */}
                <Card className="p-6 border-stone-200">
                  <h4 className="font-heading font-semibold text-stone-900 mb-3">
                    {t('prototype_context')}
                  </h4>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    {prototypeData?.context}
                  </p>
                </Card>

                {/* User Type */}
                <Card className="p-6 border-stone-200">
                  <h4 className="font-heading font-semibold text-stone-900 mb-3">
                    {t('prototype_user')}
                  </h4>
                  <p className="text-stone-600 text-sm leading-relaxed">
                    {prototypeData?.user_type}
                  </p>
                </Card>

                {/* Key Features */}
                <Card className="p-6 border-stone-200">
                  <h4 className="font-heading font-semibold text-stone-900 mb-3">
                    {t('prototype_features')}
                  </h4>
                  <ul className="space-y-2">
                    {prototypeData?.key_features?.map((feature, index) => (
                      <li key={index} className="text-stone-600 text-sm flex items-start gap-2">
                        <span className="text-primary font-semibold">•</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </Card>

                {/* Visual Style & Tools */}
                <Card className="p-6 border-stone-200">
                  <h4 className="font-heading font-semibold text-stone-900 mb-3">
                    {t('prototype_style')}
                  </h4>
                  <p className="text-stone-600 text-sm mb-4">
                    {prototypeData?.visual_style}
                  </p>
                  <h4 className="font-heading font-semibold text-stone-900 mb-2">
                    {t('prototype_tools')}
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {/* Prioritize Gemini (Google) */}
                    <a
                      href="https://gemini.google.com/app"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-primary text-white shadow-sm hover:bg-primary/90 hover:shadow-md transition-all cursor-pointer"
                    >
                      Gemini
                      <ExternalLink className="w-3 h-3" />
                    </a>

                    {/* Render other suggested tools as clickable tags with links when known */}
                    {prototypeData?.suggested_tools
                      ?.filter((tool) => tool.toLowerCase().indexOf("gemini") === -1)
                      .map((tool, index) => {
                        const lower = tool.toLowerCase();
                        let href = null;

                        if (lower.includes("midjourney")) href = "https://www.midjourney.com/";
                        else if (lower.includes("dall")) href = "https://openai.com/dall-e-3";
                        else if (lower.includes("figma")) href = "https://www.figma.com/";
                        else if (lower.includes("framer")) href = "https://www.framer.com/";
                        else if (lower.includes("canva")) href = "https://www.canva.com/";

                        const Tag = href ? "a" : "span";

                        return (
                          <Tag
                            key={index}
                            {...(href
                              ? {
                                  href,
                                  target: "_blank",
                                  rel: "noopener noreferrer",
                                }
                              : {})}
                            className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs bg-stone-100 text-stone-700 hover:bg-stone-200 hover:shadow-sm transition-all cursor-pointer"
                          >
                            {tool}
                            {href && <ExternalLink className="w-3 h-3 opacity-70" />}
                          </Tag>
                        );
                      })}
                  </div>
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrototypeStep;
